import { Container } from '@acies/core';
import Icon from './icon';
import { ICONS } from './icons';
import InputFieldEvent from './input-field-event';
import TextInput from './text-input';
import TextInputEvent from './text-input-event';
import theme from './theme';

export default class InputField extends Container {
	public static override observedAttributes = [...Container.observedAttributes, 'icon', 'placeholder', 'text', 'maximum-characters'];
	/**
	 * The autoCapitalize property sets whether what the user types is turned into capitals.
	 *
	 * False by default, so an InputField keeps what it is given. Set it to true for a field whose content is written in capitals whatever the user does — a key or a code, say — so they do not have to hold shift to type one.
	 *
	 * The text is capitalised as it is typed, before the InputFieldEvent reports it, so a listener never sees the lower case version.
	 *
	 * @param {boolean} value True if typed text should be turned into capitals. default is false
	 */
	public set autoCapitalize(value: boolean) {
		if (this.#autoCapitalize === value) {
			return;
		}
		assertIsBoolean(value);
		this.#autoCapitalize = value;
	}

	public get autoCapitalize(): boolean {
		return this.#autoCapitalize;
	}

	/**
	 * The blurOnEnter property sets if the InputField lets go of the focus once Enter or NumpadEnter has been released in it. See Input.blurOnEnter for when to turn it off.
	 *
	 * @param {boolean} value True if the InputField blurs after Enter or false. default is true
	 */
	public set blurOnEnter(value: boolean) {
		this.#inputChild.blurOnEnter = value;
	}

	public get blurOnEnter(): boolean {
		return this.#inputChild.blurOnEnter;
	}

	/**
	 * The decimals property sets if a numeric InputField accepts a decimal point. It has no effect when type is 'text'.
	 *
	 * @param {boolean} value True if a decimal point is accepted or false. default is true
	 */
	public set decimals(value: boolean) {
		this.#inputChild.decimals = value;
	}

	public get decimals(): boolean {
		return this.#inputChild.decimals;
	}

	/**
	 * The enabled property sets if the InputField is interactive or not.
	 *
	 * @param {boolean} value True if the InputField is interactive or false. default is true
	 */
	public set enabled(value: boolean) {
		if (this.#enabled === value) {
			return;
		}
		assertIsBoolean(value);
		this.#enabled = value;
		this.#enabledChanged = true;
		this.invalidateProperties();
	}

	public get enabled(): boolean {
		return this.#enabled;
	}

	/**
	 * The error property will cause a strong red border and the state changes to "error" when set to true.
	 *
	 * When the user focuses the InputField in the error state, the error flag will be set to false and the red border will disappear.
	 */
	public set error(value: boolean) {
		if (this.#error === value) {
			return;
		}
		assertIsBoolean(value);
		this.#error = value;
		this.#errorChanged = true;
		this.invalidateProperties();
	}

	public get error(): boolean {
		return this.#error;
	}

	/**
	 * The icon is shown to the left and is helpful if you want to add further visual information.
	 *
	 * @param value The name of the icon you want to show.
	 */
	public set icon(value: keyof typeof ICONS | null) {
		if (this.#icon === value) {
			return;
		}
		assertIsValidIconName(value);
		this.#icon = value;
		this.#iconChanged = true;
		this.invalidateProperties();
	}

	public get icon(): keyof typeof ICONS | null {
		return this.#icon;
	}

	/**
	 * The maximumCharacters property, sets the maximum allowed characters to be typed or pasted into the Input by the user.
	 *
	 * If you want to "unset" the maximumCharacters, give Number.NaN as the parameter.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/maxLength} for syntax
	 */
	public set maximumCharacters(value: number) {
		this.#inputChild.maximumCharacters = value;
	}

	public get maximumCharacters(): number {
		return this.#inputChild.maximumCharacters;
	}

	/**
	 * The negatives property sets if a numeric InputField accepts a leading minus sign. It has no effect when type is 'text'.
	 *
	 * @param {boolean} value True if negative numbers are accepted or false. default is false
	 */
	public set negatives(value: boolean) {
		this.#inputChild.negatives = value;
	}

	public get negatives(): boolean {
		return this.#inputChild.negatives;
	}

	/**
	 * The placeholder property defines the text displayed in input-field when it has no text.
	 *
	 * The placeholder text should provide a brief hint to the user as to the expected type of data that should be entered into the input-field.
	 */
	public set placeholder(value: string) {
		this.#inputChild.placeholder = value;
	}

	public get placeholder(): string {
		return this.#inputChild.placeholder;
	}

	public set readOnly(value: boolean) {
		if (this.#readOnly === value) {
			return;
		}
		assertIsBoolean(value);
		this.#readOnly = value;
		this.#readOnlyChanged = true;
		this.invalidateProperties();
	}

	public get readOnly(): boolean {
		return this.#readOnly;
	}

	/**
	 * The selectOnFocus property sets if the whole text is selected when the InputField takes the focus, so what the user types replaces it. See Input.selectOnFocus for when it is selected and when not.
	 *
	 * @param {boolean} value True if the text is selected when the InputField takes the focus or false. default is false
	 */
	public set selectOnFocus(value: boolean) {
		this.#inputChild.selectOnFocus = value;
	}

	public get selectOnFocus(): boolean {
		return this.#inputChild.selectOnFocus;
	}

	/** @returns The current state */
	public get state(): 'disabled' | 'error' | 'focused' | 'hovered' | 'idle' | 'readonly' {
		return this.#state;
	}

	public set text(value: string) {
		this.#inputChild.text = value;
		// the look of the state the field is in rather than the idle look: setting the text leaves a
		// read-only, disabled or focused field what it was. An error is cleared below, and clearing it
		// restyles the field once more
		this.stateChanged();
		this.error = false;
	}

	public get text(): string {
		return this.#inputChild.text;
	}

	/**
	 * The text-align CSS property sets the horizontal alignment of the inline-level content inside a block element. This means it works like vertical-align but in the horizontal direction.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/text-align} for syntax
	 */
	public set textAlign(value: 'center' | 'left' | 'right') {
		this.#inputChild.textAlign = value;
	}

	public get textAlign(): 'center' | 'left' | 'right' {
		return this.#inputChild.textAlign;
	}

	/**
	 * The type property is used when you want to enforce numeric validation for the text property or user input.
	 *
	 * If the type is set to 'numeric', validation of the values will be enforced, otherwise not.
	 */
	public set type(value: 'numeric' | 'text') {
		this.#inputChild.type = value;
	}

	public get type(): 'numeric' | 'text' {
		return this.#inputChild.type;
	}

	#autoCapitalize: boolean;
	#enabled: boolean;
	#enabledChanged: boolean;
	#error: boolean;
	#errorChanged: boolean;
	#focused: boolean;
	#hasEventListeners: boolean;
	#hovered: boolean;
	#icon: keyof typeof ICONS | null;
	#iconChanged: boolean;
	#iconChild: Icon;
	#inputChild: TextInput;
	#readOnly: boolean;
	#readOnlyChanged: boolean;
	#state: 'disabled' | 'error' | 'focused' | 'hovered' | 'idle' | 'readonly';

	public constructor() {
		super();
		this.alignment = 'center';
		this.cornerRadius = 4;
		this.fill = theme.colors.fill.weak;
		this.gapHorizontal = 10;
		this.minHeight = 32;
		this.minWidth = 32;
		this.paddingHorizontal = 10;
		this.stroke = theme.strokes.soft;
		this.#autoCapitalize = false;
		this.#error = false;
		this.#errorChanged = false;
		this.#enabled = true;
		this.#enabledChanged = false;
		this.#focused = false;
		this.#hasEventListeners = false;
		this.#hovered = false;
		this.#icon = null;
		this.#iconChanged = false;
		this.#iconChild = new Icon();
		this.#iconChild.visible = false;
		this.#readOnly = false;
		this.#readOnlyChanged = false;
		this.#state = 'idle';
		this.#inputChild = new TextInput();
	}

	protected override attributeChangedCallback(name: string, _: string, value: string) {
		super.attributeChangedCallback(name, _, value);
		if (name === 'icon') {
			this.#iconAttributeChanged(value);
		} else if (name === 'placeholder') {
			this.#placeholderAttributeChanged(value);
		} else if (name === 'text') {
			this.#textAttributeChanged(value);
		} else if (name === 'maximum-characters') {
			this.#maximumCharactersAttributeChanged(value);
		}
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#iconChanged) {
			this.#commitIcon();
		}
		if (this.#enabledChanged || this.#readOnlyChanged) {
			this.#commitEnabledOrReadOnly();
		}
		if (this.#errorChanged) {
			this.#commitError();
		}
	}

	/**
	 * Moves focus to the TextInput this holds.
	 *
	 * An InputField is a Container around one, so a caller asking the field to take focus means the field it shows, not the box around it.
	 */
	public override focus(options?: FocusOptions): void {
		this.#inputChild.focus(options);
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponents([this.#iconChild, this.#inputChild]);
		this.#addEventListeners();
	}

	/**
	 * Forgets that the pointer was over the field and that it had the focus, because the browser will not always say so itself.
	 *
	 * No pointerleave is fired when a hovered element, or an ancestor of it, is removed from the document, and no pointerenter when it is put back under a pointer that has not moved since — measured in Chromium rather than assumed. A field taken out of the document while hovered would therefore come back out of a
	 * list's pool still hovered, and stay so until the pointer next passed over it. So the flag is dropped here, the way InteractiveContainer drops its own.
	 *
	 * The focus is dropped too. Chromium blurs a focused element that is removed, and says so, so there the field is idle before it gets here — but not every browser does, and a field that heard no blur would come back out of the pool dressed as focused while the focus is somewhere else. A move with moveBefore() keeps
	 * the focus and never gets here: Component answers it with connectedMoveCallback() instead.
	 *
	 * Only the hovered and focused states are left. A disabled, read-only or erroneous field is held in its state by other means.
	 */
	protected override disconnected(): void {
		super.disconnected();
		this.#hovered = false;
		this.#focused = false;
		if (this.#state === 'hovered' || this.#state === 'focused') {
			this.#invokeIfStateChanged('idle');
		}
	}

	/**
	 * The stateChanged method will be invoked every time the state changes.
	 *
	 * Override the method in sub classes to specify how the Component will behave or be styled.
	 *
	 * Hovering is not styled: the cursor is what says the field can be typed in, so a hovered field is dressed as an idle one. Neither is dressed while the field is in error, so the error look stays on under the pointer and after it has left.
	 */
	protected stateChanged(): void {
		if ((this.state === 'idle' || this.state === 'hovered') && !this.error) {
			this.#setIdleStyles();
		} else if (this.state === 'focused') {
			this.#setFocusedStyles();
		} else if (this.state === 'disabled') {
			this.#setDisabledStyles();
		} else if (this.state === 'error') {
			this.#setErrorStyles();
		} else if (this.state === 'readonly') {
			this.#setReadonlyStyles();
		}
	}

	#addEventListeners(): void {
		if (!this.#hasEventListeners) {
			this.addEventListener(TextInputEvent.FOCUSED, this.#eventsListener as EventListener);
			this.addEventListener(TextInputEvent.BLURRED, this.#eventsListener as EventListener);
			this.addEventListener(TextInputEvent.ENTER, this.#eventsListener as EventListener);
			this.addEventListener(TextInputEvent.CHANGED, this.#eventsListener as EventListener);
			this.addEventListener('pointerenter', this.#eventsListener as EventListener);
			this.addEventListener('pointerleave', this.#eventsListener as EventListener);
			this.#hasEventListeners = true;
		}
	}

	/**
	 * Turns what the field holds into capitals, when it is a field that does that.
	 *
	 * Writing the text back moves the caret to the end, which is why this only runs for a field that asked for it: a key is typed left to right, where a sentence is not.
	 */
	#capitalizeText(): void {
		if (this.#autoCapitalize) {
			this.text = this.text.toUpperCase();
		}
	}

	#commitEnabledOrReadOnly(): void {
		this.#enabledChanged = false;
		this.#readOnlyChanged = false;
		this.#inputChild.enabled = this.#enabled;
		this.#inputChild.readOnly = this.#readOnly;
		if (this.#readOnly && this.#enabled) {
			this.#removeEventListeners();
			this.#invokeIfStateChanged('readonly');
		} else if (!this.#readOnly && this.#enabled) {
			this.#addEventListeners();
			this.#invokeIfStateChanged('idle');
		} else if (!this.#readOnly && !this.#enabled) {
			this.#removeEventListeners();
			this.#invokeIfStateChanged('disabled');
		} else if (this.#readOnly && !this.#enabled) {
			this.#removeEventListeners();
			this.#invokeIfStateChanged('disabled');
		}
	}

	#commitError(): void {
		this.#errorChanged = false;
		if (this.error) {
			this.#invokeIfStateChanged('error');
		} else {
			this.#invokeIfStateChanged('idle');
		}
	}

	#commitIcon(): void {
		this.#iconChanged = false;
		if (this.icon === null) {
			this.#iconChild.visible = false;
			this.#iconChild.name = null;
		} else {
			this.#iconChild.visible = true;
			this.#iconChild.name = this.icon;
		}
	}

	#dispatchEvents(event: PointerEvent | TextInputEvent): void {
		if (event instanceof TextInputEvent) {
			this.#dispatchInputFieldEvents(event);
		} else if (event instanceof PointerEvent) {
			this.#dispatchPointerEvents(event);
		}
	}

	#dispatchInputFieldEvents(event: TextInputEvent): void {
		if (event.type === TextInputEvent.BLURRED) {
			this.dispatchEvent(new InputFieldEvent(InputFieldEvent.BLURRED, this.text));
		} else if (event.type === TextInputEvent.CHANGED) {
			// before the event rather than after, so a listener is told what the field now holds
			// and never has to capitalise it a second time
			this.#capitalizeText();
			this.dispatchEvent(new InputFieldEvent(InputFieldEvent.CHANGED, this.text));
		} else if (event.type === TextInputEvent.FOCUSED) {
			this.dispatchEvent(new InputFieldEvent(InputFieldEvent.FOCUSED, this.text));
		} else if (event.type === TextInputEvent.ENTER) {
			this.dispatchEvent(new InputFieldEvent(InputFieldEvent.ENTER, this.text));
		}
	}

	#dispatchPointerEvents(event: PointerEvent): void {
		if (event.type === 'pointerenter') {
			this.dispatchEvent(new InputFieldEvent(InputFieldEvent.HOVERED, this.text));
		} else if (event.type === 'pointerleave') {
			this.dispatchEvent(new InputFieldEvent(InputFieldEvent.UN_HOVERED, this.text));
		}
	}

	#eventsListener(event: PointerEvent | TextInputEvent): void {
		event.stopPropagation();
		if (event.type === TextInputEvent.FOCUSED) {
			this.#focused = true;
			// we set the private #error to false and not error,
			// so we don't trigger a state change
			this.#error = false;
		} else if (event.type === TextInputEvent.BLURRED) {
			this.#focused = false;
		}
		if (event.type === 'pointerenter') {
			this.#hovered = true;
		} else if (event.type === 'pointerleave') {
			this.#hovered = false;
		}
		const state: 'focused' | 'hovered' | 'idle' = this.#getStateFromFlags();
		this.#invokeIfStateChanged(state);
		this.#dispatchEvents(event);
	}

	#getStateFromFlags(): 'focused' | 'hovered' | 'idle' {
		if (this.#focused) {
			return 'focused';
		} else if (this.#hovered) {
			return 'hovered';
		}
		return 'idle';
	}

	#iconAttributeChanged(value: string): void {
		assertIsValidIconName(value);
		this.icon = value;
	}

	#invokeIfStateChanged(value: 'disabled' | 'error' | 'focused' | 'hovered' | 'idle' | 'readonly'): void {
		if (this.#state !== value) {
			this.#state = value;
			this.stateChanged();
		}
	}

	#maximumCharactersAttributeChanged(value: string): void {
		this.maximumCharacters = Number.parseInt(value, 10);
	}

	#placeholderAttributeChanged(value: string): void {
		this.placeholder = value;
	}

	#removeEventListeners(): void {
		if (this.#hasEventListeners) {
			this.removeEventListener(TextInputEvent.FOCUSED, this.#eventsListener as EventListener);
			this.removeEventListener(TextInputEvent.BLURRED, this.#eventsListener as EventListener);
			this.removeEventListener(TextInputEvent.CHANGED, this.#eventsListener as EventListener);
			this.removeEventListener('pointerenter', this.#eventsListener as EventListener);
			this.removeEventListener('pointerleave', this.#eventsListener as EventListener);
			this.#hasEventListeners = false;
		}
	}

	#setDisabledStyles(): void {
		this.fill = theme.colors.fill.weak;
		this.stroke = null;
		this.#iconChild.variant = 'disabled';
		this.#inputChild.fill = theme.colors.text.disabled;
	}

	#setErrorStyles(): void {
		this.fill = theme.colors.error.lighter;
		this.stroke = theme.strokes.error;
		this.#iconChild.variant = 'default';
		this.#inputChild.fill = theme.colors.text.sub;
	}

	#setFocusedStyles(): void {
		this.fill = theme.colors.fill.focus;
		this.stroke = theme.strokes.focus;
		this.#iconChild.variant = 'default';
		this.#inputChild.fill = theme.colors.text.sub;
	}

	#setIdleStyles(): void {
		this.fill = theme.colors.fill.weak;
		this.stroke = theme.strokes.soft;
		this.#iconChild.variant = 'default';
		this.#inputChild.fill = theme.colors.text.sub;
	}

	#setReadonlyStyles(): void {
		this.fill = theme.colors.fill.weak;
		this.stroke = null;
		this.#iconChild.variant = 'default';
		this.#inputChild.fill = theme.colors.text.sub;
	}

	#textAttributeChanged(value: string): void {
		this.text = value;
	}
}
customElements.define('ac-input-field', InputField);

function assertIsBoolean(value: unknown): asserts value is boolean {
	if (typeof value === 'boolean') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be true or false`);
}

function assertIsValidIconName(value: unknown): asserts value is keyof typeof ICONS | null {
	if ((typeof value === 'string' && value in ICONS) || value === null) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type string and in ICONS object or null`);
}
